import React, { useEffect, useMemo, useState } from "react";
import useFilterStatus from "../hook/useFilterStatus";
import {
	CommonListFunctionsType,
	useExtendCommonListFunctions,
} from "../../../hooks/useCommonList/useCommonListFunctions";
import { AdminOrderExtractor, AdminOrderExtraParams, AdminOrderParams } from "../hook/ListOrder";
import {
	AdminOrderManagementFilterItem,
	AdminTransactionAddButtton,
	BookingFilterButton,
	BookingFilterSelect,
	BookingFilterTitle,
} from "../styled/DesStyled";
import { SelectOptionType } from "react-select";
import { SmMultipleSelectBox } from "../../../controls/components/selectBox/SelectBox";
import DateRangeInput from "../../../controls/components/dateRangeInput/DateRangeInput";
import DialogAddOrderAdmin from "../AdminBooking/AddOrderAdmin";
import PaymentLinkDialog from "../AdminBooking/PaymentLinkDialog";
import { AdminFilterStoreControl } from "../AdminBooking/FilterPartner";
import { AdminFilterUserControl } from "../AdminBooking/FilterUser";
import useExportListOrder from "../hook/ExportExcelOrder";
import exportToExcel from "../hook/exportToExcel";
import {
	OnepayPaymentMethodLabelMap,
	OrderMethod,
	OrderMethodLabelMap,
	OrderStatusLabelMap,
	PriceDetail,
} from "../../../../models/Order";
import moment from "moment";
import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";
import SearchPanel from "../../User/component/SearchPanel";
import { getCountryOptions } from "../../../../services/CountryRatesService";
import { getListDestinations } from "../../../../services/LocationService";
import Location from "../../../../models/Location";
import styled from "styled-components";
import { formatOrderCreatedAtForStation } from "../../../../utils/orderDateDisplay";
import { formatOrderCalendarDate } from "../../../../utils/orderCalendarDate";
import { normalizeCountryKey, toCanonicalCountryName } from "../../CountryRates/countryCatalog";

// Export guard: the booking list paginates at 20 rows/page, so countPages * 20
// approximates how many rows an export will pull. Warn before exporting very
// large result sets (e.g. "all time") so a heavy file isn't a surprise.
const EXPORT_LIST_PAGE_SIZE = 20;
const EXPORT_HEAVY_ROW_THRESHOLD = 10000;

export default function BookingFilter(props: { reload: () => void; countPages: number }) {
	const {
		filterStore,
		filterToDate,
		filterFromDate,
		filterStates,
		filterActive,
		filterUserId,
		filterFromReplyDate,
		filterToReplyDate,
		filterCountry,
		filterProvice,
		filterKeyword,
		doFilterStore,
		doChangeFilterField,
		doFilterUserId,
		doFilterKeyword,
	} = useExtendCommonListFunctions(
		AdminOrderExtractor,
		AdminOrderExtraParams,
		(nFilter: AdminOrderParams, nFunctions: CommonListFunctionsType) => ({
			...nFilter,
			...nFunctions,
		})
	);
	const [isDialogLoginOpen, setIsDialogLoginOpen] = useState(false);
	const [isPaymentLinkOpen, setIsPaymentLinkOpen] = useState(false);
	const [countryOptions, setCountryOptions] = useState<string[]>([]);
	const [destinations, setDestinations] = useState<Location[]>([]);
	const { status, setStatus, statusOptions } = useFilterStatus({
		filterStatus: (filterStates && filterStates) || undefined,
	});
	const [loading, setLoading] = useState(false);
	const { openAlertDialog } = useAlertDialog();

	const canonicalCountryOptions = useMemo(() => {
		const byKey = new Map<string, string>();

		[...countryOptions, ...destinations.map((destination) => String(destination?.country || "").trim())].forEach(
			(countryRaw) => {
				const trimmed = String(countryRaw || "").trim();
				if (!trimmed) return;
				const key = normalizeCountryKey(trimmed);
				if (!key || byKey.has(key)) return;
				byKey.set(key, toCanonicalCountryName(trimmed) || trimmed);
			}
		);

		return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
	}, [countryOptions, destinations]);

	useEffect(() => {
		let cancelled = false;

		const loadFilterOptions = async () => {
			try {
				const [countries, destinationResult] = await Promise.all([
					getCountryOptions(),
					getListDestinations(1, 500),
				]);

				if (cancelled) return;
				setCountryOptions(countries || []);
				setDestinations(destinationResult?.data || []);
			} catch (error) {
				if (!cancelled) {
					setCountryOptions([]);
					setDestinations([]);
				}
			}
		};

		loadFilterOptions();

		return () => {
			cancelled = true;
		};
	}, []);

	const filteredDestinations = useMemo(() => {
		const normalizedCountry = normalizeCountryKey(filterCountry || "");
		return destinations
			.filter((destination) => {
				if (!destination?.id) return false;
				if (!normalizedCountry) return true;
				return normalizeCountryKey(destination.country || "") === normalizedCountry;
			})
			.sort((a, b) => `${a.name || ""}`.localeCompare(`${b.name || ""}`));
	}, [destinations, filterCountry]);

	const doChangeStatusString = (value: any) => {
		doChangeFilterField?.({
			filterStates: value,
		});
	};

	const changeStartFromDate = (date: Date | null) => {
		doChangeFilterField?.({
			filterFromDate: date,
		});
	};

	const changeStartToDate = (date: Date | null) => {
		doChangeFilterField?.({
			filterToDate: date,
		});
	};

	const changeEndFromDate = (date: Date | null) => {
		doChangeFilterField?.({
			filterFromReplyDate: date,
		});
	};

	const changeEndToDate = (date: Date | null) => {
		doChangeFilterField?.({
			filterToReplyDate: date,
		});
	};

	const openAdd = () => {
		setIsDialogLoginOpen(true);
	};
	const { loadListOrder } = useExportListOrder({ countPages: props.countPages });
	const handleExport = async () => {
		setLoading(true);
		try {
			const data = await loadListOrder(
				filterStates?.split(";"),
				filterFromDate,
				filterToDate,
				filterStore,
				filterUserId,
				filterFromReplyDate,
				filterToReplyDate,
				filterCountry,
				filterProvice,
				filterKeyword,
				filterActive
			);

			if (!data || data.length === 0) {
				openAlertDialog?.(
					AlertType.Fail,
					"Không xuất được dữ liệu. Có thể bộ lọc hiện tại không có đơn nào, " +
						"hoặc kết nối bị gián đoạn giữa chừng khi tải. Vui lòng thử lại hoặc thu hẹp bộ lọc.",
					undefined,
					undefined,
					undefined,
					{ title: "Xuất Excel" }
				);
				return;
			}

			const formattedData = data?.map((val: PriceDetail) => ({
				"Booking ID": `${val.id}`,
				Country: `${val.countryName}`,
				City: `${val.cityName}`,
				Station: `${val.partner?.name || ""}`,
				"Full name": `${val.customer?.fullName || ""}`,
				Email: `${val.email}`,
				"Country code": `${val.customer?.phonePrefix || ""}`,
				Phone: `${val.phone}`,
				"No of bags": val.quantity || 0,
				Status: OrderStatusLabelMap.get(val?.status || 0),
				"Payment Method": val?.paymentMethodSelect
					? val.paymentMethodSelect
					: val?.paymentMethod === OrderMethod.OnePay
					? OnepayPaymentMethodLabelMap.get(val?.subPaymentMethod || 0) || "Không xác định"
					: OrderMethodLabelMap.get(val?.paymentMethod || 0) || "Không xác định",

				"Transaction ID":
					val.transaction?.onepayPaymentId ||
					val.transaction?.paypalPaymentId ||
					val.transaction?.stripePaymentId ||
					val.onepayPaymentId ||
					val.paypalPaymentId ||
					val.stripePaymentId ||
					0,
				"Drop-off Date": val.timeInString
					? formatOrderCalendarDate(val.timeInString)
					: val.timeIn
					? formatOrderCalendarDate(val.timeIn)
					: "",
				"Pick-up date": val.timeOutString
					? formatOrderCalendarDate(val.timeOutString)
					: val.timeOut
					? formatOrderCalendarDate(val.timeOut)
					: "",
				// "Drop-off Time": val.timeOutString ? moment(val.timeOutString).format("HH:mm") : val.timeOut ? moment(val.timeOut).format("HH:mm") : "",
				// "Pick-up Time": val.timeInString ? moment(val.timeInString).format("HH:mm") : val.timeIn ? moment(val.timeIn).format("HH:mm") : "",
				"Total Amount": Math.round((val.totalMoney || 0) * 100) / 100,
				"Station's Revenue": Math.round(2.25 * (val.quantity || 0) * (val.distanceDay || 0) * 100) / 100,
				"Created Date": formatOrderCreatedAtForStation(val.createdAt, val.timeZoneId),
			}));

			exportToExcel(formattedData, "report.xlsx");
		} catch (error) {
			openAlertDialog?.(
				AlertType.Fail,
				"Đã xảy ra lỗi khi xuất Excel. Vui lòng thử lại, hoặc thu hẹp bộ lọc nếu dữ liệu quá lớn.",
				undefined,
				undefined,
				undefined,
				{ title: "Lỗi" }
			);
		} finally {
			setLoading(false);
		}
	};
	const onExcel = async () => {
		const estimatedRows = (props.countPages || 0) * EXPORT_LIST_PAGE_SIZE;
		const heavy = estimatedRows >= EXPORT_HEAVY_ROW_THRESHOLD;
		const message = heavy
			? `Bạn sắp xuất khoảng ${estimatedRows.toLocaleString("vi-VN")} dòng. ` +
			  `File có thể nặng và mất một lúc để tải về. Nếu chỉ cần một giai đoạn, ` +
			  `hãy lọc theo "Filter by start time" hoặc "Filter by end time" trước khi xuất.\n\n` +
			  `Vẫn tiếp tục xuất toàn bộ?`
			: `Bạn chắc chắn muốn xuất Excel?`;
		openAlertDialog?.(
			AlertType.Confirm,
			message,
			() => {},
			() => handleExport(),
			undefined,
			{
				declineText: "Hủy bỏ",
				acceptText: heavy ? "Xuất toàn bộ" : "Xác nhận",
			}
		);
	};

	const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const nextCountry = event.target.value || null;
		const selectedDestination = destinations.find(
			(destination) => `${destination.id || ""}` === `${filterProvice || ""}`
		);
		const destinationMatchesCountry =
			!nextCountry ||
			normalizeCountryKey(selectedDestination?.country || "") === normalizeCountryKey(nextCountry);

		doChangeFilterField?.({
			filterCountry: nextCountry,
			filterProvice: destinationMatchesCountry ? filterProvice : null,
		});
	};

	const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		doChangeFilterField?.({
			filterProvice: event.target.value || null,
		});
	};

	const handleStationStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		doChangeFilterField?.({
			filterActive: event.target.value || null,
		});
	};

	return (
		<>
			<BookingFilterGrid>
				<BookingFilterGridItem>
					<BookingFilterTitle>Filter by facility</BookingFilterTitle>
					<AdminFilterStoreControl
						filterStaffId={filterStore || ""}
						includeInactive
						doChangeStaffId={doFilterStore}
					/>
				</BookingFilterGridItem>
				<BookingFilterGridItem>
					<BookingFilterTitle>Filter by user</BookingFilterTitle>
					<AdminFilterUserControl
						filterStaffId={filterUserId || ""}
						doChangeStaffId={doFilterUserId}
					/>
				</BookingFilterGridItem>
				<BookingFilterGridItem>
					<BookingFilterTitle>Filter by status</BookingFilterTitle>
					<AdminOrderManagementFilterInput
						options={statusOptions}
						value={status}
						setvalue={(value) => {
							setStatus(value);
							doChangeStatusString(value.join(";"));
						}}
					/>
				</BookingFilterGridItem>
				<BookingFilterGridItem>
					<BookingFilterTitle>Filter by country</BookingFilterTitle>
					<BookingFilterSelect
						value={filterCountry || ""}
						onChange={handleCountryChange}>
						<option value="">All countries</option>
						{canonicalCountryOptions.map((country) => (
							<option
								key={country}
								value={country}>
								{country}
							</option>
						))}
					</BookingFilterSelect>
				</BookingFilterGridItem>
				<BookingFilterGridItem>
					<BookingFilterTitle>Filter by city</BookingFilterTitle>
					<BookingFilterSelect
						value={filterProvice || ""}
						onChange={handleCityChange}>
						<option value="">All cities</option>
						{filteredDestinations.map((destination) => (
							<option
								key={destination.id || destination.name || ""}
								value={destination.id || ""}>
								{destination.name || ""}
							</option>
						))}
					</BookingFilterSelect>
				</BookingFilterGridItem>
				<BookingFilterSidebar>
					<BookingFilterGridItem>
						<BookingFilterTitle>Filter by station status</BookingFilterTitle>
						<BookingFilterSelect
							value={filterActive || ""}
							onChange={handleStationStatusChange}>
							<option value="">All stations</option>
							<option value="true">Active stations</option>
							<option value="false">Inactive stations</option>
						</BookingFilterSelect>
					</BookingFilterGridItem>
					<BookingFilterGridItem>
						<BookingFilterTitle>Lọc theo mã đơn</BookingFilterTitle>
						<SearchPanel
							placeholder="Tìm kiếm"
							filterKeyword={filterKeyword}
							doFilterKeyword={doFilterKeyword}
						/>
					</BookingFilterGridItem>
					<BookingFilterGridItem $alignEnd={true}>
						<BookingFilterActions>
							<BookingFilterButton onClick={openAdd}>Add</BookingFilterButton>
							<BookingFilterButton onClick={() => setIsPaymentLinkOpen(true)}>Payment link</BookingFilterButton>
							<AdminTransactionAddButtton
								onClick={onExcel}
								disabled={loading}>
								{loading ? "Đang xuất..." : "Export Excel"}
							</AdminTransactionAddButtton>
						</BookingFilterActions>
					</BookingFilterGridItem>
				</BookingFilterSidebar>
				<BookingFilterGridItem $span={2}>
					<BookingFilterTitle>Filter by start time</BookingFilterTitle>
					<AdminOrderManagementFilterDate
						fromDate={(filterFromDate && filterFromDate) || null}
						toDate={(filterToDate && filterToDate) || null}
						doFilterFromDate={changeStartFromDate}
						doFilterToDate={changeStartToDate}
					/>
				</BookingFilterGridItem>
				<BookingFilterGridItem $span={2}>
					<BookingFilterTitle>Filter by end time</BookingFilterTitle>
					<AdminOrderManagementFilterDate
						fromDate={(filterFromReplyDate && filterFromReplyDate) || null}
						toDate={(filterToReplyDate && filterToReplyDate) || null}
						doFilterFromDate={changeEndFromDate}
						doFilterToDate={changeEndToDate}
					/>
				</BookingFilterGridItem>
			</BookingFilterGrid>

			<DialogAddOrderAdmin
				openDialog={isDialogLoginOpen}
				setOpenDialog={setIsDialogLoginOpen}
				reload={props.reload}
			/>
			<PaymentLinkDialog
				openDialog={isPaymentLinkOpen}
				setOpenDialog={setIsPaymentLinkOpen}
			/>
		</>
	);
}

export function AdminOrderManagementFilterInput(props: {
	options: SelectOptionType[];
	value: string[];
	setvalue: (val: string[]) => void;
}) {
	return (
		<SmMultipleSelectBox
			options={props.options}
			value={props.value}
			onChange={props.setvalue}></SmMultipleSelectBox>
	);
}

export function AdminOrderManagementFilterDate(props: {
	fromDate: Date | null;
	toDate: Date | null;
	doFilterFromDate?: (date: Date | null) => void;
	doFilterToDate?: (date: Date | null) => void;
}) {
	const handleChangeFromDate = (date: Date | null) => {
		props.doFilterFromDate?.(date);
	};

	const handleChangeToDate = (date: Date | null) => {
		props.doFilterToDate?.(date);
	};

	return (
		<DateRangeInput
			fromDate={props.fromDate}
			toDate={props.toDate}
			onDateFromChange={handleChangeFromDate}
			onDateToChange={handleChangeToDate}
		/>
	);
}

const BookingFilterGrid = styled.div`
	display: grid;
	width: 1200px;
	position: relative;
	margin: 8px auto 14px;
	max-width: 1440px;
	grid-template-columns: repeat(5, minmax(0, 1fr)) minmax(240px, 1.2fr);
	gap: 18px 16px;
	align-items: start;

	@media screen and (max-width: 1282px) {
		width: calc(100% - 32px);
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media screen and (max-width: 768px) {
		width: 100%;
		padding: 0 16px;
		grid-template-columns: 1fr;
	}
`;

const BookingFilterGridItem = styled(AdminOrderManagementFilterItem)<{ $span?: number; $alignEnd?: boolean }>`
	margin: 0;
	min-width: 0;
	grid-column: span ${({ $span }) => $span || 1};
	align-self: ${({ $alignEnd }) => ($alignEnd ? "end" : "start")};

	@media screen and (max-width: 1282px) {
		grid-column: span ${({ $span }) => ($span && $span > 1 ? 2 : 1)};
	}

	@media screen and (max-width: 768px) {
		grid-column: span 1;
	}
`;

const BookingFilterSidebar = styled.div`
	display: grid;
	grid-row: span 2;
	grid-template-rows: auto auto 1fr;
	gap: 16px;
	align-self: stretch;

	@media screen and (max-width: 1282px) {
		grid-row: auto;
		gap: 18px;
	}
`;

const BookingFilterActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	min-height: 100%;

	@media screen and (max-width: 1282px) {
		justify-content: flex-start;
	}

	@media screen and (max-width: 768px) {
		justify-content: flex-start;
		flex-wrap: wrap;
	}
`;
